import _ from 'lodash';
import { Address, AddressType, CountryType, PlaceOfServiceType, ProviderType, StateType } from '../../api';
import { rosterIsClaimLocked, rosterIsoDate } from './roster.claims';
import { rosterNpiWithCheckDigit } from './roster.identifiers';
import { rosterCountByType, rosterQueryFacilities, rosterQueryProviders } from './roster.query';
import type {
    RosterApplyResult,
    RosterCapabilities,
    RosterClaim,
    RosterClaimStatus,
    RosterDataSource,
    RosterFacility,
    RosterFacilityDraft,
    RosterListQuery,
    RosterPage,
    RosterProvider,
    RosterProviderDraft,
    RosterRecord,
    RosterRecordRef,
    RosterTab,
    RosterTag,
    RosterTypeCounts,
} from './roster.types';

/**
 * An in-memory Roster, standing in for the API until plan step 9 wires the real one (FE-109).
 *
 * **Everything here is synthetic, and must look it to anyone who checks.** The design's prototype
 * seeded its table with what appear to be real customer organisations; none of those are used.
 * Organisations are invented, e-mail addresses are on `example.com` (RFC 2606), phone numbers
 * are in the 555-01xx block reserved for fiction, NPIs are Luhn-valid in the never-issued
 * 2-series (see `generateNpi` in the provider app for why), and EINs use the `00` prefix, which
 * the IRS has never assigned. The screen shows a "Mock data" banner while this is the source,
 * and it is only reachable behind the Roster feature flag (NS-4).
 *
 * Seeded and deterministic: the same seed and reference date always produce the same roster,
 * so a screenshot, a bug report and a test all describe the same records.
 *
 * Claims follow the lifecycle (NS-15): a recent date of service is still BILLED, only older
 * ones can be ACKNOWLEDGED, DENIED or PAID.
 */

export interface MockRosterOptions {
    seed?: number;
    /** Simulated network time per call. Tests pass 0. */
    latencyMs?: number;
    /** The clock — "today" for generated dates and for `updated` on save. */
    now?: () => Date;
    providerCount?: number;
    facilityCount?: number;
    /**
     * Narrow what the mock offers to what the app's real API can do — the provider app's is
     * read-only with no tags — so a demo never shows an affordance production does not have (NS-4).
     */
    capabilities?: Partial<RosterCapabilities>;
}

const FIRST_NAMES = [
    'Avery',
    'Blake',
    'Camila',
    'Dana',
    'Elena',
    'Farah',
    'Gavin',
    'Hana',
    'Isaac',
    'Jonah',
    'Keira',
    'Leon',
    'Maya',
    'Nolan',
    'Olive',
    'Priya',
    'Quinn',
    'Rosa',
    'Samir',
    'Tessa',
    'Uma',
    'Victor',
    'Wren',
    'Yusuf',
];
const LAST_NAMES = [
    'Abbott',
    'Barlow',
    'Castellano',
    'Delaney',
    'Ellison',
    'Fairbanks',
    'Galloway',
    'Holloway',
    'Ingram',
    'Jarvis',
    'Kendrick',
    'Lockhart',
    'Marlowe',
    'Nakashima',
    'Okafor',
    'Pemberton',
    'Quintero',
    'Rowland',
    'Sorensen',
    'Thackeray',
    'Underhill',
    'Vasquez',
    'Whitfield',
    'Yardley',
];
const ORGANIZATIONS = [
    'Brightpath Behavioral Health GA',
    'Brightpath Behavioral Health NC',
    'Cedar Hollow Therapy Group',
    'Harborview Pediatric Therapy',
    'Lantern Autism Services SC',
    'Maple Ridge Developmental Care',
    'Northfield Family Therapy',
    'Silverbrook Behavior Partners',
];
const INDIVIDUAL_TYPES = [
    ProviderType.RENDERING,
    ProviderType.REFERRING,
    ProviderType.SUPERVISING,
    ProviderType.ORDERING,
];
/** Public NUCC taxonomy codes — a code set, not an identifier of anyone. */
const TAXONOMY_CODES = ['103K00000X', '207N00000X', '207R00000X', '363L00000X', '261QM0801X'];
const TAG_NAMES = ['credentialed', 'medicaid', 'pending-revalidation', 'telehealth'];
const STREETS = [
    'Alder Court',
    'Birchwood Lane',
    'Copper Mill Road',
    'Dogwood Trail',
    'Elm Park Drive',
    'Foxglove Way',
    'Granite Ridge Road',
    'Heron Bay Drive',
    'Juniper Street',
    'Kingfisher Lane',
    'Linden Avenue',
    'Magnolia Bend',
];
const CITIES: [string, StateType, string][] = [
    ['Greensboro', StateType.NC, '27455'],
    ['Savannah', StateType.GA, '31401'],
    ['Valdosta', StateType.GA, '31602'],
    ['Macon', StateType.GA, '31217'],
    ['Charlotte', StateType.NC, '28214'],
    ['Durham', StateType.NC, '27704'],
    ['Spartanburg', StateType.SC, '29307'],
    ['Columbia', StateType.SC, '29201'],
];
const PLACES_OF_SERVICE = [
    PlaceOfServiceType.OFFICE,
    PlaceOfServiceType.HOME,
    PlaceOfServiceType.OUTPATIENTHOSPITAL,
    PlaceOfServiceType.INDEPENDENTCLINIC,
    PlaceOfServiceType.SKILLEDNURSINGFACILITY,
    PlaceOfServiceType.TELEHEALTHPROVIDEDINPATIENTSHOME,
];
const PAYERS = ['Aetna', 'Cigna', 'Humana', 'UnitedHealthcare', 'BCBS of North Carolina', 'Georgia Medicaid'];

const DAY_MS = 24 * 60 * 60 * 1000;

type Random = () => number;

/** mulberry32 — small, fast, and good enough for fixtures. */
function seeded(seed: number): Random {
    let state = seed >>> 0;
    return () => {
        state = (state + 0x6d2b79f5) >>> 0;
        let t = state;
        t = Math.imul(t ^ (t >>> 15), t | 1);
        t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
}

function hashSeed(text: string): number {
    let hash = 2166136261;
    for (let i = 0; i < text.length; i += 1) {
        hash ^= text.charCodeAt(i);
        hash = Math.imul(hash, 16777619);
    }
    return hash >>> 0;
}

const pick = <T>(random: Random, items: T[]): T => items[Math.floor(random() * items.length)];
const chance = (random: Random, p: number): boolean => random() < p;
const digits = (random: Random, count: number): string =>
    Array.from({ length: count }, () => Math.floor(random() * 10)).join('');
const between = (random: Random, min: number, max: number): number => min + Math.floor(random() * (max - min + 1));

function mockNpi(random: Random): string {
    return rosterNpiWithCheckDigit(`2${digits(random, 8)}`);
}

function mockEin(random: Random): string {
    return `00${digits(random, 7)}`;
}

function mockAddress(random: Random, name: string): Address {
    const [city, state, postal] = pick(random, CITIES);
    const lines = [`${between(random, 10, 2400)} ${pick(random, STREETS)}`];
    if (chance(random, 0.2)) {
        lines.push(`Suite ${between(random, 100, 480)}`);
    }
    return {
        type: chance(random, 0.75) ? AddressType.MAILING : AddressType.PHYSICAL,
        name,
        lines,
        city,
        state,
        postal,
        country: CountryType.US,
    };
}

/** `created` somewhere in the two years before `now`, `updated` between it and `now`. */
function mockTimestamps(random: Random, now: Date): { created: string; updated: string } {
    const created = now.getTime() - between(random, 30, 730) * DAY_MS;
    const updated = created + random() * (now.getTime() - created);
    return { created: new Date(created).toISOString(), updated: new Date(updated).toISOString() };
}

export function generateMockProviders(random: Random, count: number, now: Date, tags: RosterTag[]): RosterProvider[] {
    return Array.from({ length: count }, (_unused, index) => {
        const isOrganization = chance(random, 0.28);
        const first = pick(random, FIRST_NAMES);
        const last = pick(random, LAST_NAMES);
        const organization = isOrganization ? pick(random, ORGANIZATIONS) : undefined;
        const type = isOrganization
            ? chance(random, 0.65)
                ? ProviderType.BILLING
                : pick(random, INDIVIDUAL_TYPES)
            : pick(random, INDIVIDUAL_TYPES);
        const displayName = organization || `${first} ${last}`;
        return {
            id: `prv_${String(index + 1).padStart(4, '0')}${digits(random, 4)}`,
            type,
            organization,
            first: isOrganization ? undefined : first,
            last: isOrganization ? undefined : last,
            suffix: !isOrganization && chance(random, 0.12) ? 'MD' : undefined,
            npi: mockNpi(random),
            ein: isOrganization ? mockEin(random) : undefined,
            ptan: chance(random, 0.3) ? digits(random, 6) : undefined,
            license: chance(random, 0.4) ? `LIC-${digits(random, 6)}` : undefined,
            taxonomyCode: pick(random, TAXONOMY_CODES),
            taxonomyQualifier: 'ZZ',
            email: isOrganization
                ? `billing${index + 1}@example.com`
                : `${first}.${last}${index + 1}@example.com`.toLowerCase(),
            // E.164, in the 555-0100–0199 block reserved for fiction.
            phone: chance(random, 0.5) ? `+14045550${between(random, 100, 199)}` : undefined,
            tags: chance(random, 0.28) ? [pick(random, tags)] : [],
            halt: chance(random, 0.07),
            // Automation is a BILLING-provider setting; the support dialog clears it for every other type.
            automation:
                type === ProviderType.BILLING
                    ? { professional: chance(random, 0.7), payment: chance(random, 0.55) }
                    : undefined,
            addresses: chance(random, 0.65) ? [mockAddress(random, displayName)] : [],
            ...mockTimestamps(random, now),
        };
    });
}

export function generateMockFacilities(random: Random, count: number, now: Date, tags: RosterTag[]): RosterFacility[] {
    return Array.from({ length: count }, (_unused, index) => {
        const address = mockAddress(random, '');
        const name = `${address.lines[0]} ${String(between(random, 10, 99))}`;
        address.name = name;
        return {
            id: `fac_${String(index + 1).padStart(4, '0')}${digits(random, 4)}`,
            name,
            nickname: chance(random, 0.3) ? `${address.city} site` : undefined,
            npi: chance(random, 0.55) ? mockNpi(random) : undefined,
            ein: chance(random, 0.3) ? mockEin(random) : undefined,
            other: chance(random, 0.15) ? digits(random, 6) : undefined,
            placeOfServiceCode: pick(random, PLACES_OF_SERVICE),
            tags: chance(random, 0.15) ? [pick(random, tags)] : [],
            addresses: [address],
            ...mockTimestamps(random, now),
        };
    });
}

/**
 * Claims that reference one record, newest date of service first.
 *
 * Status follows how long ago the service was (NS-15): under two weeks it has only been billed;
 * under five weeks it may be acknowledged; only after that can it be adjudicated — denied or
 * paid.
 */
export function generateMockClaims(recordId: string, now: Date): RosterClaim[] {
    const random = seeded(hashSeed(recordId));
    const count = between(random, 9, 34);
    const claims: RosterClaim[] = Array.from({ length: count }, () => {
        const daysAgo = between(random, 1, 240);
        const dos = new Date(now.getFullYear(), now.getMonth(), now.getDate() - daysAgo);
        let status: RosterClaimStatus = 'BILLED';
        if (daysAgo >= 35) {
            const roll = random();
            status = roll < 0.6 ? 'PAID' : roll < 0.8 ? 'DENIED' : 'ACKNOWLEDGED';
        } else if (daysAgo >= 14) {
            status = chance(random, 0.5) ? 'ACKNOWLEDGED' : 'BILLED';
        }
        return {
            id: `69${digits(random, 15)}`,
            dos: rosterIsoDate(dos),
            patientName: `${pick(random, FIRST_NAMES)} ${pick(random, LAST_NAMES)}`,
            payerName: pick(random, PAYERS),
            chargeAmount: `${between(random, 80, 1480)}.00`,
            status,
        };
    });
    return claims.sort((a, b) => (a.dos < b.dos ? 1 : a.dos > b.dos ? -1 : 0));
}

export class MockRosterDataSource implements RosterDataSource {
    public readonly kind = 'mock' as const;

    public readonly capabilities: RosterCapabilities;

    private providers: RosterProvider[];
    private facilities: RosterFacility[];
    private tags: RosterTag[];
    private readonly claims = new Map<string, RosterClaim[]>();
    private readonly latencyMs: number;
    private readonly now: () => Date;
    private nextId = 1;
    private failNext: Error | undefined;

    constructor(options: MockRosterOptions = {}) {
        const random = seeded(options.seed ?? 7);
        this.capabilities = {
            write: true,
            applyToClaims: true,
            updatedTimestamp: true,
            tags: true,
            ...options.capabilities,
        };
        this.latencyMs = options.latencyMs ?? 300;
        this.now = options.now ?? (() => new Date());
        const today = this.now();
        this.tags = TAG_NAMES.map((name, index) => ({ id: `tag_${index + 1}`, name }));
        this.providers = generateMockProviders(random, options.providerCount ?? 168, today, this.tags);
        this.facilities = generateMockFacilities(random, options.facilityCount ?? 96, today, this.tags);
    }

    /** Make the next call reject — for exercising the screen's error states by hand. */
    public failNextRequest(error: Error = new Error('Mock request failed')): void {
        this.failNext = error;
    }

    public async listProviders(query: RosterListQuery): Promise<RosterPage<RosterProvider>> {
        await this.respond();
        return _.cloneDeep(rosterQueryProviders(this.providers, query));
    }

    public async listFacilities(query: RosterListQuery): Promise<RosterPage<RosterFacility>> {
        await this.respond();
        return _.cloneDeep(rosterQueryFacilities(this.facilities, query));
    }

    public async listByIds(tab: RosterTab, ids: string[]): Promise<RosterRecord[]> {
        await this.respond();
        const records: RosterRecord[] = tab === 'providers' ? this.providers : this.facilities;
        return _.cloneDeep(records.filter(record => ids.includes(record.id)));
    }

    public async countProvidersByType(): Promise<RosterTypeCounts> {
        await this.respond();
        return rosterCountByType(this.providers);
    }

    public async saveProvider(draft: RosterProviderDraft): Promise<RosterProvider> {
        await this.respond();
        // Mirrors the API: `npi` and `type` are required on both create and update payloads.
        if (!draft.npi) {
            throw new Error('NPI is required');
        }
        const existing = draft.id ? this.providers.find(provider => provider.id === draft.id) : undefined;
        if (draft.id && !existing) {
            throw new Error(`Provider ${draft.id} not found`);
        }
        const saved: RosterProvider = {
            ..._.cloneDeep(draft),
            id: existing?.id ?? this.newId('prv'),
            tags: existing?.tags ?? [],
            created: existing?.created ?? this.now().toISOString(),
            updated: this.now().toISOString(),
        };
        this.providers = existing
            ? this.providers.map(provider => (provider.id === saved.id ? saved : provider))
            : [saved, ...this.providers];
        return _.cloneDeep(saved);
    }

    public async saveFacility(draft: RosterFacilityDraft): Promise<RosterFacility> {
        await this.respond();
        if (!draft.name?.trim()) {
            throw new Error('Facility name is required');
        }
        const existing = draft.id ? this.facilities.find(facility => facility.id === draft.id) : undefined;
        if (draft.id && !existing) {
            throw new Error(`Facility ${draft.id} not found`);
        }
        const saved: RosterFacility = {
            ..._.cloneDeep(draft),
            id: existing?.id ?? this.newId('fac'),
            tags: existing?.tags ?? [],
            created: existing?.created ?? this.now().toISOString(),
            updated: this.now().toISOString(),
        };
        this.facilities = existing
            ? this.facilities.map(facility => (facility.id === saved.id ? saved : facility))
            : [saved, ...this.facilities];
        return _.cloneDeep(saved);
    }

    public async disable(tab: RosterTab, ids: string[]): Promise<void> {
        await this.respond();
        if (tab === 'providers') {
            this.providers = this.providers.filter(provider => !ids.includes(provider.id));
        } else {
            this.facilities = this.facilities.filter(facility => !ids.includes(facility.id));
        }
    }

    public async addTag(tab: RosterTab, ids: string[], tagName: string): Promise<void> {
        await this.respond();
        const name = tagName.trim();
        if (!name) {
            throw new Error('Tag name is required');
        }
        let tag = this.tags.find(existing => existing.name.toLowerCase() === name.toLowerCase());
        if (!tag) {
            tag = { id: `tag_${this.tags.length + 1}`, name };
            this.tags = [...this.tags, tag];
        }
        const applied = tag;
        const withTag = <T extends { id: string; tags: RosterTag[] }>(record: T): T =>
            ids.includes(record.id) && !record.tags.some(existing => existing.id === applied.id)
                ? { ...record, tags: [...record.tags, applied] }
                : record;
        if (tab === 'providers') {
            this.providers = this.providers.map(withTag);
        } else {
            this.facilities = this.facilities.map(withTag);
        }
    }

    /** The mock sorts in memory, so every column can be sorted. */
    public canSort(): boolean {
        return true;
    }

    public async listClaimsFor(ref: RosterRecordRef): Promise<RosterClaim[]> {
        await this.respond();
        return _.cloneDeep(this.claimsFor(ref));
    }

    public async applyToClaims(
        ref: RosterRecordRef,
        claimIds: string[],
        options: { retransmit: boolean },
    ): Promise<RosterApplyResult> {
        await this.respond();
        const claims = this.claimsFor(ref);
        const targets = claims.filter(claim => claimIds.includes(claim.id));
        if (targets.length !== claimIds.length) {
            throw new Error('Some selected claims do not reference this record');
        }
        // The server's job in the real flow, repeated here so the mock cannot be more permissive than it.
        if (targets.some(rosterIsClaimLocked)) {
            throw new Error('Paid claims cannot be amended');
        }
        return { updated: targets.length, queuedForTransmission: options.retransmit ? targets.length : 0 };
    }

    private claimsFor(ref: RosterRecordRef): RosterClaim[] {
        const key = `${ref.tab}:${ref.id}`;
        if (!this.claims.has(key)) {
            this.claims.set(key, generateMockClaims(ref.id, this.now()));
        }
        return this.claims.get(key)!;
    }

    private newId(prefix: string): string {
        const id = `${prefix}_new${String(this.nextId).padStart(4, '0')}`;
        this.nextId += 1;
        return id;
    }

    private async respond(): Promise<void> {
        if (this.latencyMs > 0) {
            await new Promise(resolve => setTimeout(resolve, this.latencyMs));
        }
        if (this.failNext) {
            const error = this.failNext;
            this.failNext = undefined;
            throw error;
        }
    }
}
